import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  API,
  authConfig,
  authHeaders,
  alertError,
  btnPrimary,
  btnSecondary,
  fieldStyle,
  labelStyle,
  thStyle,
  tdStyle,
  downloadMemberReportCsv,
  printReportAsPdf,
  formatDuration,
  pageTitleStyle,
  pageSubtitleStyle,
  ticketClientBranchLabel,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import TicketStatusBadge from '../components/TicketStatusBadge';
import SearchableSelect from '../../../components/SearchableSelect';
import { TicketDetailLink } from '../ticketNav';
import { WorkloadBar, SkeletonBlock } from '../../../components/module-ui';

function TicketMemberReportPage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(monthAgo);
  const [toDate, setToDate] = useState(today);
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState('');
  const [report, setReport] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/assignments/engineers`, authHeaders())
      .then((res) => setMembers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMembers([]));
  }, []);

  const memberOptions = useMemo(() => {
    const byId = new Map();
    byId.set('', { value: '', label: 'All members', searchText: 'all members' });
    for (const r of allRows) {
      if (!r?.member_id) continue;
      byId.set(String(r.member_id), {
        value: String(r.member_id),
        label: r.member_name || r.member_id,
        searchText: `${r.member_name || ''} ${r.email || ''} ${r.member_id}`,
      });
    }
    for (const m of members) {
      if (!m?.user_id) continue;
      const id = String(m.user_id);
      if (byId.has(id)) continue;
      byId.set(id, {
        value: id,
        label: `${m.name}${m.role ? ` (${m.role})` : ''}`,
        searchText: `${m.name || ''} ${m.email || ''} ${id}`,
      });
    }
    return Array.from(byId.values());
  }, [allRows, members]);

  const dateParams = useCallback((mid = memberId) => ({
    date_from: `${fromDate}T00:00:00.000Z`,
    date_to: `${toDate}T23:59:59.999Z`,
    ...(mid ? { member_id: String(mid) } : {}),
  }), [fromDate, toDate, memberId]);

  const loadReport = useCallback(async (overrideMemberId) => {
    setLoading(true);
    setError(null);
    try {
      const mid = overrideMemberId !== undefined ? overrideMemberId : memberId;
      const midStr = mid ? String(mid) : '';

      // Always load the team summary for the date range (powers the dropdown + fallback filter).
      const summaryRes = await axios.get(`${API}/reports/member`, authConfig({
        date_from: `${fromDate}T00:00:00.000Z`,
        date_to: `${toDate}T23:59:59.999Z`,
      }));
      const summaryData = summaryRes.data || {};
      const summaryRows = summaryData.rows || [];
      setAllRows(summaryRows);

      if (!midStr) {
        setReport(summaryData);
        return;
      }

      // Focused member detail (ticket list + timeline).
      const detailRes = await axios.get(`${API}/reports/member`, authConfig({
        date_from: `${fromDate}T00:00:00.000Z`,
        date_to: `${toDate}T23:59:59.999Z`,
        member_id: midStr,
      }));
      const detail = detailRes.data || {};

      // If API focus came back empty but summary has the member, keep that row visible.
      let rows = detail.rows || [];
      if (!rows.length) {
        const fromSummary = summaryRows.filter((r) => String(r.member_id) === midStr);
        if (fromSummary.length) {
          rows = fromSummary;
        }
      }
      setReport({ ...detail, rows });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load member report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, memberId]);

  useEffect(() => {
    loadReport('');
  }, []);

  const rows = report?.rows || [];
  const ticketDetails = report?.ticket_details || [];
  const events = report?.events || [];

  const handleMemberChange = (value) => {
    setMemberId(value == null ? '' : String(value));
  };

  const handleExportCsv = async () => {
    try {
      await downloadMemberReportCsv(dateParams());
    } catch (err) {
      alert(err.response?.data?.detail || 'CSV export failed');
    }
  };

  const handlePrintPdf = () => {
    printReportAsPdf(
      'Member Activity Report',
      [
        { label: 'Date range', value: `${fromDate} to ${toDate}` },
        { label: 'Member', value: report?.member_name || memberOptions.find((o) => o.value === memberId)?.label || 'All members' },
        { label: 'Tickets handled', value: report?.tickets_handled ?? rows.reduce((s, r) => s + (r.tickets_handled || 0), 0) },
        { label: 'Closed events', value: report?.tickets_closed ?? rows.reduce((s, r) => s + (r.tickets_closed || 0), 0) },
        { label: 'Total active time', value: formatDuration(report?.total_active_seconds ?? rows.reduce((s, r) => s + (r.total_active_seconds || 0), 0)) },
      ],
      document.getElementById('ticket-member-report'),
    );
  };

  const statCard = (label, value, color = '#002FA7') => (
    <div style={{ padding: '10px', background: '#F9FAFB', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
      <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: '700', color }}>{value ?? '—'}</div>
    </div>
  );

  return (
    <div id="ticket-member-report">
      <h2 style={pageTitleStyle}>Member Activity Report</h2>
      <p style={pageSubtitleStyle}>
        Full history for a user in a date range — tickets handled, closures, active time per ticket, and every work event.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end', marginBottom: '12px' }}>
        <div>
          <label style={labelStyle}>From Date</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>To Date</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={fieldStyle} />
        </div>
        <div style={{ minWidth: '260px', flex: '1 1 260px', position: 'relative', zIndex: 5 }}>
          <label style={labelStyle}>Member</label>
          <SearchableSelect
            options={memberOptions}
            value={memberId}
            onChange={handleMemberChange}
            placeholder="All members…"
            searchPlaceholder="Search member by name…"
            aria-label="Member"
          />
        </div>
        <button type="button" onClick={() => loadReport()} disabled={loading} style={btnPrimary}>
          {loading ? 'Loading…' : 'Apply'}
        </button>
        <button type="button" onClick={handleExportCsv} style={btnSecondary}>Export CSV</button>
        <button type="button" onClick={handlePrintPdf} style={btnSecondary}>Print / PDF</button>
      </div>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}

      {loading && !report ? <SkeletonBlock rows={5} /> : null}

      {!memberId && rows.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <WorkloadBar
            title="Tickets handled by member"
            data={rows.map((r) => ({
              name: r.member_name || r.member_id,
              value: r.tickets_handled || 0,
            }))}
          />
        </div>
      )}

      {memberId && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginBottom: '14px' }}>
          {statCard('Tickets Handled', report?.tickets_handled ?? rows[0]?.tickets_handled ?? 0)}
          {statCard('Closed (events)', report?.tickets_closed ?? rows[0]?.tickets_closed ?? 0, '#059669')}
          {statCard('Active Time', formatDuration(report?.total_active_seconds ?? rows[0]?.total_active_seconds ?? 0), '#2563EB')}
          {statCard('Work Events', events.length, '#7C3AED')}
        </div>
      )}

      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>Summary by Member</h3>
      {rows.length === 0 && !loading ? (
        <p style={{ color: '#6B7280' }}>No member activity for selected range.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                <th style={thStyle}>Member</th>
                <th style={thStyle}>Handled</th>
                <th style={thStyle}>Open</th>
                <th style={thStyle}>In Progress</th>
                <th style={thStyle}>Completed</th>
                <th style={thStyle}>Closed</th>
                <th style={thStyle}>Closed Events</th>
                <th style={thStyle}>Active Time</th>
                <th style={thStyle}>Events</th>
                <th style={thStyle}>Transfers In/Out</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.member_id || r.member_name} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={tdStyle}>
                    <button
                      type="button"
                      onClick={() => {
                        const id = String(r.member_id || '');
                        setMemberId(id);
                        loadReport(id);
                      }}
                      style={{ background: 'none', border: 'none', color: '#002FA7', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: '12px' }}
                      title="Focus this member"
                    >
                      {r.member_name}
                    </button>
                    {r.email ? <div style={{ color: '#6B7280', fontSize: '11px' }}>{r.email}</div> : null}
                  </td>
                  <td style={tdStyle}>{r.tickets_handled ?? r.total_assigned}</td>
                  <td style={tdStyle}>{r.open_count ?? 0}</td>
                  <td style={tdStyle}>{r.in_progress_count ?? 0}</td>
                  <td style={tdStyle}>{r.completed_count ?? 0}</td>
                  <td style={tdStyle}>{r.closed_count ?? 0}</td>
                  <td style={tdStyle}>{r.tickets_closed ?? 0}</td>
                  <td style={tdStyle}>{formatDuration(r.total_active_seconds)}</td>
                  <td style={tdStyle}>{r.events_count ?? 0}</td>
                  <td style={tdStyle}>{(r.transfers_in ?? 0)} / {(r.transfers_out ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ticketDetails.length > 0 && (
        <>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>
            Ticket Detail {report?.member_name ? `— ${report.member_name}` : ''}
          </h3>
          <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={thStyle}>Ticket No</th>
                  <th style={thStyle}>Subject</th>
                  <th style={thStyle}>Client / Branch</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Domain</th>
                  <th style={thStyle}>Priority</th>
                  <th style={thStyle}>Active Time</th>
                  <th style={thStyle}>Events</th>
                  <th style={thStyle}>Closed in Range</th>
                  <th style={thStyle}>First / Last Activity</th>
                  <th style={thStyle}>Event Types</th>
                </tr>
              </thead>
              <tbody>
                {ticketDetails.map((t) => (
                  <tr key={t.ticket_no} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={tdStyle}>
                      <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: '600' }}>
                        {t.ticket_no}
                      </TicketDetailLink>
                    </td>
                    <td style={tdStyle}>{t.subject || '—'}</td>
                    <td style={tdStyle}>{ticketClientBranchLabel(t)}</td>
                    <td style={tdStyle}><TicketStatusBadge status={t.status} /></td>
                    <td style={tdStyle}>{t.domain || '—'}</td>
                    <td style={tdStyle}>{t.priority || '—'}</td>
                    <td style={tdStyle}>{formatDuration(t.active_seconds)}</td>
                    <td style={tdStyle}>{t.events_count}</td>
                    <td style={tdStyle}>{t.closed_in_range ? 'Yes' : 'No'}</td>
                    <td style={tdStyle}>
                      {formatUtcToIst(t.first_event_at) || '—'}
                      <br />
                      {formatUtcToIst(t.last_event_at) || '—'}
                    </td>
                    <td style={tdStyle}>{t.event_summary || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {events.length > 0 && (
        <>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>Activity Timeline</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={thStyle}>Time (IST)</th>
                  <th style={thStyle}>Event</th>
                  <th style={thStyle}>Ticket</th>
                  <th style={thStyle}>Details</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => (
                  <tr key={`${ev.ticket_no}-${ev.timestamp}-${i}`} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={tdStyle}>{formatUtcToIst(ev.timestamp)}</td>
                    <td style={tdStyle}>{ev.event_type}</td>
                    <td style={tdStyle}>
                      <TicketDetailLink ticketNo={ev.ticket_no} style={{ color: '#002FA7' }}>
                        {ev.ticket_no}
                      </TicketDetailLink>
                    </td>
                    <td style={tdStyle}>{ev.details && Object.keys(ev.details).length ? JSON.stringify(ev.details) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default TicketMemberReportPage;
